import React, {useEffect, useState} from "react";
import {Col, Row} from "reactstrap";
import Dialog from "../../base/dialog";
import Divider from "@mui/material/Divider";
import Button from "../../base/button/button";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Form from "../../base/form";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import {OutlinedDropdownForm} from "../../base/dropdown/outlined_dropdown";


const schema = Yup.object().shape({
    category: Yup.object().nullable().required(ValidateMessages.required),
})


const EditGiftCardCategoryModal = ({
                                       open, setOpen, onSuccess, giftCard,
                                       giftCardCategories: giftCardCategoriesProp,
                                   }) => {
    const [submitting, setSubmitting] = useState(false);
    const [giftCardCategories, setGiftCardCategories] = useState([]);
    const validate = makeValidate(schema);
    const required = makeRequired(schema);
    const isMounted = useIsMounted();
    const initialValues = {category: giftCard?.category}

    /**
     * Loads the gift card categories as soon as the component mounts
     */
    useEffect(() => {
        if (!giftCardCategoriesProp) {
            getGiftCardCategories();
        }
    }, [])

    /**
     * Listens for the changes of categories prop and with each change, sets the state
     */
    useEffect(() => {
        if (!giftCardCategoriesProp) return;
        setGiftCardCategories(giftCardCategoriesProp);
    }, [giftCardCategoriesProp])


    /**
     * Fetches the list of gift card categories from the server and if the result of the api is successful, sets the
     * list.
     */
    const getGiftCardCategories = () => {
        Api.getGiftCardCategories().then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setGiftCardCategories(response.data);
            }
        })
    }

    /**
     * Updates the gift card category by calling the server api and if the result of the api is successful, calls
     * the onSuccess callback.
     * @param values {any} from values
     */
    const updateGiftCardCategory = (values) => {
        setSubmitting(true);
        const forApi = {
            id: giftCard.id,
            category: values.category
        }
        Api.updateGiftCardCategory(forApi).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                onSuccess({...giftCard, category: values.category});
                setOpen(false);
            }
            setSubmitting(false);
        })
    }

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'sm'}
        >
            <Row className='no-gutters justify-content-center align-items-start w-100'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                        Update Category
                    </p>
                </Row>

                <Divider className='divider'/>
                <Form
                    className='w-100'
                    onSubmit={updateGiftCardCategory}
                    validate={validate}
                    initialValues={initialValues}
                    render={({values, form}) => {
                        return (
                            <Row className='no-gutters justify-content-around w-100'>
                                <Row className='no-gutters justify-content-around w-100 mx-4'>
                                    <Col xs={12} className={'my-3 mb-4 w-100'}>
                                        <p className={'text-color-3 text-sm'}>
                                            Please select the new category for the selected gift card.
                                        </p>
                                    </Col>
                                    <Col xs={12} className='px-2 mb-4 d-flex align-items-center justify-content-center'>
                                        <OutlinedDropdownForm
                                            fontSize={14}
                                            fullWidth={true}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                            label={'Category'}
                                            name={'category'}
                                            required={!!required.category}
                                            data={giftCardCategories
                                                ?.map(e => ({
                                                    value: e,
                                                    label: e.name,
                                                    id: e.id,
                                                })) ?? []}
                                        />
                                    </Col>
                                </Row>
                                <div className='my-2 w-100'/>
                                <Divider className='divider'/>
                                <Row className='no-gutters justify-content-between align-items-start w-100'>
                                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                                        <Button
                                            disabled={!open || submitting}
                                            variant={'dialog'}
                                            color={'primary'}
                                            onClick={() => setOpen(false)}
                                            borderRadius={'0 8px 0 8px'}
                                        >
                                            {submitting ? 'submitting...' : 'Close'}
                                        </Button>
                                        <Button
                                            disabled={!open || submitting || values.category?.id === giftCard?.category?.id}
                                            variant={'dialog'}
                                            color={'primary'}
                                            type={'submit'}
                                            borderRadius={'8px 0 8px 0'}
                                        >
                                            {submitting ? 'submitting...' : 'Update'}
                                        </Button>
                                    </Row>
                                </Row>
                            </Row>
                        );
                    }}
                />
            </Row>
        </Dialog>
    );
}

export default EditGiftCardCategoryModal;
